import {
  createContext,
  useContext,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import {
  IconCaretDownFilled,
  IconCaretUpDown,
  IconCaretUpFilled,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import style from "./Table.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import { Button } from "@base-ui/react/button";

type Variant = "default" | "card";
type Align = "left" | "center" | "right";

const TableContext = createContext<{ variant: Variant }>({
  variant: "default",
});

type TableProps = HTMLAttributes<HTMLTableElement> & {
  children: ReactNode;
  variant?: Variant;
  expandable?: boolean;
  rowCount?: number;
  collapsedRows?: number;
  stickyFirstColumn?: boolean;
};

export function Table({
  children,
  className,
  variant = "default",
  expandable = false,
  rowCount = 0,
  collapsedRows = 5,
  stickyFirstColumn = false,
  ...rest
}: TableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const canExpand = expandable && rowCount > collapsedRows;

  const tableEl = (
    <table
      className={classnames(
        style.table,
        stickyFirstColumn && style.stickyFirstColumn,
      )}
      {...rest}
    >
      {children}
    </table>
  );

  return (
    <TableContext.Provider value={{ variant }}>
      <div
        className={classnames(
          style.tableWrapper,
          variant === "card" && style.tableWrapperCard,
          canExpand && !isExpanded && style.tableCollapsed,
          className,
        )}
      >
        {tableEl}
        {canExpand && (
          <Button
            type="button"
            className={style.expandButton}
            onClick={() => setIsExpanded((v) => !v)}
            aria-expanded={isExpanded}
            aria-label={
              isExpanded ? "Réduire la liste" : "Afficher toutes les lignes"
            }
          >
            {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
          </Button>
        )}
      </div>
    </TableContext.Provider>
  );
}

function Header({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  const { variant } = useContext(TableContext);
  return (
    <thead
      className={classnames(
        style.thead,
        variant === "card" && style.theadCard,
        className,
      )}
      {...rest}
    >
      <tr>{children}</tr>
    </thead>
  );
}

function TBody({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

function TR({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr className={classnames(style.tr, className)} {...rest}>
      {children}
    </tr>
  );
}

type CellProps = Omit<TdHTMLAttributes<HTMLTableCellElement>, "align"> & {
  align?: Align;
};

function TD({ children, align, className, ...rest }: CellProps) {
  return (
    <td
      className={classnames(style.td, className, align && style[align])}
      {...rest}
    >
      {children}
    </td>
  );
}

export type SortDirection = "asc" | "desc";

type HeaderCellProps = Omit<ThHTMLAttributes<HTMLTableCellElement>, "align"> & {
  align?: Align;
  sortDirection?: SortDirection | null;
  onSort?: () => void;
};

function SortIcon({ direction }: { direction: SortDirection | null }) {
  if (direction === "asc") return <IconCaretUpFilled size={16} />;
  if (direction === "desc") return <IconCaretDownFilled size={16} />;
  return <IconCaretUpDown size={16} stroke={1.5} />;
}

interface SortButtonProps {
  direction?: SortDirection | null;
  onSort: () => void;
  children: ReactNode;
}

function SortButton({ direction = null, onSort, children }: SortButtonProps) {
  return (
    <Button className={style.sortButton} onClick={onSort}>
      {children}
      <SortIcon direction={direction} />
    </Button>
  );
}

function TH({
  children,
  align = "left",
  className,
  sortDirection = null,
  onSort,
  ...rest
}: HeaderCellProps) {
  const { variant } = useContext(TableContext);
  return (
    <th
      className={classnames(
        style.th,
        variant === "card" && style.thCard,
        className,
        style[align],
      )}
      aria-sort={
        sortDirection === "asc"
          ? "ascending"
          : sortDirection === "desc"
          ? "descending"
          : undefined
      }
      {...rest}
    >
      {onSort ? (
        <SortButton direction={sortDirection} onSort={onSort}>
          {children}
        </SortButton>
      ) : (
        children
      )}
    </th>
  );
}

Table.Header = Header;
Table.Column = TH;
Table.Body = TBody;
Table.Row = TR;
Table.Cell = TD;
Table.SortButton = SortButton;
